import axios, { AxiosError } from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { ApiResponse, ApiError } from '@/types';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: import.meta.env.VITE_API_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    this.api.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('accessToken');

        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
          // El token de Microsoft/Google OAuth es el mismo que se usa para Authorization
          // El backend requiere x-ms-token para endpoints de emails
          config.headers['x-ms-token'] = token;
        }

        // El módulo de correo guarda la compañía en `emailCompanyId` (authService /
        // emailService) y el resto de la app en `selectedCompanyId`; si solo existe
        // una, las peticiones de correo salían sin x-company-id → 400/403.
        const companyId = localStorage.getItem('selectedCompanyId') || localStorage.getItem('emailCompanyId');
        if (companyId && config.headers) {
          config.headers['x-company-id'] = companyId;
        }

        // Enviar un correo con adjuntos sube hasta 25 MB (el tope del backend).
        // Con los 30 s de la instancia, una subida doméstica de 2-5 Mbps no
        // alcanza a terminar: axios aborta a mitad del cuerpo, el POST nunca
        // llega completo al backend —Cloud Run no registra ni la petición, así
        // que en los logs solo queda el OPTIONS del preflight— y el usuario ve
        // "no se pudo enviar" sin ninguna causa rastreable. El tiempo lo manda
        // la conexión del usuario, no el servidor, así que estas peticiones
        // llevan su propio margen.
        if (config.data instanceof FormData) {
          config.timeout = 300_000;
        }

        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // El backend responde 401 con code EMAIL_REAUTH_REQUIRED (y header
        // x-email-reauth-required) cuando la conexión con el buzón caducó o fue
        // revocada: refrescar el JWT no lo arregla, hay que volver a conectar el
        // correo. Antes se confundía con un JWT vencido, se gastaba un refresh y
        // se cerraba la sesión sin explicar nada.
        const reauthRequired =
          error.response?.status === 401 &&
          ((error.response.data as any)?.code === 'EMAIL_REAUTH_REQUIRED' ||
            error.response.headers?.['x-email-reauth-required'] === 'true');
        if (reauthRequired) {
          window.dispatchEvent(new CustomEvent('email:reauth-required', {
            detail: { message: (error.response?.data as any)?.message },
          }));
          return Promise.reject(this.handleError(error));
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          const refreshToken = localStorage.getItem('refreshToken');
          if (!refreshToken) {
            // Sin refresh token no hay nada que refrescar: antes se llamaba igual a
            // /auth/refresh (400 "Refresh token not provided") y cada petición
            // fallaba en cadena. Cerrar la sesión de correo y avisar a la app.
            localStorage.removeItem('accessToken');
            localStorage.removeItem('expiresAt');
            window.dispatchEvent(new CustomEvent('email:session-expired'));
            return Promise.reject(this.handleError(error));
          }

          try {
            const response = await axios.post(
              //`${import.meta.env.VITE_API_URL}/api/auth/refresh`,
              `${import.meta.env.VITE_API_URL}/auth/refresh`,
              { refreshToken }
            );

            const tokenData = response.data?.data || response.data;
            const { accessToken, refreshToken: newRefreshToken, expiresIn } = tokenData;

            if (accessToken) {
              localStorage.setItem('accessToken', accessToken);
            }
            if (newRefreshToken) {
              localStorage.setItem('refreshToken', newRefreshToken);
            }
            if (expiresIn) {
              localStorage.setItem('expiresAt', String(Date.now() + expiresIn * 1000));
            }

            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              originalRequest.headers['x-ms-token'] = accessToken;
            }

            return this.api(originalRequest);
          } catch (refreshError) {
            // Solo limpiar tokens si el backend rechazó el refresh (4xx = sesión muerta).
            // Ante errores de red o 5xx los tokens siguen siendo válidos: no cerrar sesión.
            const status = (refreshError as AxiosError).response?.status ?? 0;
            if (status >= 400 && status < 500) {
              localStorage.removeItem('accessToken');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('expiresAt');
              window.dispatchEvent(new CustomEvent('email:session-expired'));
            }
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(this.handleError(error));
      }
    );
  }

  private handleError(error: AxiosError): ApiError {
    if (error.response) {
      return {
        code: error.code || 'UNKNOWN_ERROR',
        message: (error.response.data as any)?.message || error.message,
        details: error.response.data,
        statusCode: error.response.status,
      };
    } else if (error.request) {
      return {
        code: 'NETWORK_ERROR',
        message: 'No se pudo conectar con el servidor',
        statusCode: 0,
      };
    } else {
      return {
        code: 'REQUEST_ERROR',
        message: error.message,
        statusCode: 0,
      };
    }
  }

  async get<T>(url: string, params?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.get(url, { params });
      // El backend devuelve { success: true, data: {...} }
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message,
        };
      }
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error as ApiError,
      };
    }
  }

  async post<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    try {
      // Si es FormData, dejar que axios configure el Content-Type automáticamente
      const config: any = {};
      if (data instanceof FormData) {
        config.headers = {
          'Content-Type': 'multipart/form-data',
        };
      }
      
      const response = await this.api.post(url, data, config);
      // El backend devuelve { success: true, data: {...} }
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message,
        };
      }
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error as ApiError,
      };
    }
  }

  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.put(url, data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error as ApiError,
      };
    }
  }

  async patch<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.patch(url, data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error as ApiError,
      };
    }
  }

  async delete<T>(url: string, config?: { data?: any }): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.delete(url, config);
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message,
        };
      }
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error as ApiError,
      };
    }
  }

  getAxiosInstance(): AxiosInstance {
    return this.api;
  }
}

export default new ApiService();
